import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
// cloudSync (and through it the Supabase client) is imported on demand: it
// only ever runs for a signed-in player.
import type * as CloudSync from "@/lib/cloudSync";

// ═══════════════════════════════════════════════════════════════
// CloudSyncBridge — background reconciliation, no UI.
// ═══════════════════════════════════════════════════════════════
//
// Mounted once inside <AuthProvider>. Drives the cloud-sync side
// channel for signed-in users. Renders nothing.
//
//   • On sign-in: pull cloud → merge into local stores, then push
//     local → cloud so the user's new device adopts whatever was
//     already on the cloud AND backs up anything new the user
//     played anonymously before signing in.
//   • While signed-in: every 60 seconds, plus on tab-hide and
//     beforeunload, diff `localSignature()` against the last
//     pushed snapshot. If different, push.
//   • On sign-out: stop. Don't wipe local — the player can keep
//     playing anonymously.
//
// Runtime gameplay reads stay entirely local. This bridge is the
// only thing that talks to the cloud progress tables.

const SYNC_INTERVAL_MS = 60_000;

export default function CloudSyncBridge() {
  const { user } = useAuth();
  const lastSigRef = useRef<string>("");
  const inFlightRef = useRef<boolean>(false);

  useEffect(() => {
    if (!user) {
      lastSigRef.current = "";
      return;
    }
    const userId = user.id;
    let cancelled = false;
    let sync: typeof CloudSync | null = null;

    const flush = async (force = false) => {
      if (!sync || inFlightRef.current) return;
      const { localSignature, pushToCloud } = sync;
      const sig = localSignature();
      if (!force && sig === lastSigRef.current) return;
      inFlightRef.current = true;
      try {
        await pushToCloud(userId);
        if (!cancelled) lastSigRef.current = sig;
      } catch (e) {
        if (import.meta.env.DEV) console.warn("[cloudSync] push failed", e);
      } finally {
        inFlightRef.current = false;
      }
    };

    // Initial sequence: pull then push.
    (async () => {
      try {
        sync = await import("@/lib/cloudSync");
        if (cancelled) return;
        await sync.pullFromCloud(userId);
        if (cancelled) return;
        lastSigRef.current = sync.localSignature();
        await flush(true);
      } catch (e) {
        if (import.meta.env.DEV) console.warn("[cloudSync] initial sync failed", e);
      }
    })();

    const interval = window.setInterval(() => void flush(), SYNC_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === "hidden") void flush();
    };
    const onBeforeUnload = () => { void flush(); };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [user]);

  return null;
}
